import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { getNextStepTemplates, updateNextStepTemplates } from '../services/api';
import { PROJECT_STATUS_ORDER, projectStatus, projectStatusLabel } from '../lib/workStatus';

const STATUSES = [...PROJECT_STATUS_ORDER, 'on_hold'];

export const NextStepTemplatesDialog = ({ open, onOpenChange, t, onSaved }) => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getNextStepTemplates()
      .then((data) => setTemplates(data.templates || {}))
      .catch(() => toast.error('Neizdevās ielādēt veidnes'))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const data = await updateNextStepTemplates(templates);
      toast.success('Veidnes saglabātas');
      onSaved?.(data.templates || {});
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt veidnes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="next-step-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#1F3D2B]" /> Veidnes “Kas notiks tālāk?”</DialogTitle>
          <DialogDescription>
            Sagatavojiet tekstu katram statusam. Mainot objekta statusu, lauks “Kas notiks tālāk?” aizpildīsies automātiski (pirms saglabāšanas to var pielabot). Tukša veidne = bez automātiskas aizpildes.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><div className="w-6 h-6 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {STATUSES.map((status) => (
              <div key={status} className="space-y-1.5" data-testid={`next-step-template-row-${status}`}>
                <Label className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${projectStatus(status).className}`}>{projectStatusLabel(t, status)}</span>
                </Label>
                <Textarea
                  rows={2}
                  value={templates[status] || ''}
                  onChange={(e) => setTemplates((prev) => ({ ...prev, [status]: e.target.value }))}
                  placeholder={`Piem.: ko klients var sagaidīt, kad objekts ir “${projectStatusLabel(t, status)}”`}
                  data-testid={`next-step-template-${status}`}
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="next-step-templates-cancel">Atcelt</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="next-step-templates-save">
                {saving ? 'Saglabā...' : 'Saglabāt veidnes'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NextStepTemplatesDialog;
